import {
  FeesResponseType,
  TiersLimitsVolumeType,
  TwoFAType,
  UserSettingsType,
  UserType
} from 'data/Auth/types'
import { UpdateEmailResponseType } from 'data/Form/Profile/types'
import { TOtpConfigType, TwoFactorFormType } from 'data/Form/TwoFactor/types'
import { RawTransferType } from 'data/Historical/types'
import { ExtraKYCContext, Node } from 'data/types'
import omit from 'lodash/omit'
import fetchApi from 'utils/fetchApi'
import { getApiUrl } from 'utils/urlBuilder'

export const EMAIL_VERIFICATION_CODE_LENGTH = '5'

type Create2FABody = {
  code?: string
  password?: string
  type: TwoFAType
}
export const create2FA = (body: Create2FABody) =>
  fetchApi<TOtpConfigType>({
    auth: true,
    body: omit(body, 'type'),
    method: 'PUT',
    url: getApiUrl(`nabu-gateway/users/two-factor-auth/${body.type.toLocaleLowerCase()}/create`)
  })

type Enable2FABody = {
  code?: string
  emailCode?: string
  type: TwoFAType
}
export const enable2FA = (body: Enable2FABody) =>
  fetchApi<void>({
    auth: true,
    body: omit(body, 'type'),
    method: 'PUT',
    url: getApiUrl(`nabu-gateway/users/two-factor-auth/${body.type.toLocaleLowerCase()}/enable`)
  })

type Disable2FABody = {
  code: string
  type: TwoFAType
}
export const disable2FA = (body: Disable2FABody) =>
  fetchApi<void>({
    auth: true,
    body: { code: body.code },
    method: 'PUT',
    url: getApiUrl(`nabu-gateway/users/two-factor-auth/${body.type}/disable`)
  })

export const current = () =>
  fetchApi<UserType>({
    auth: true,
    method: 'GET',
    url: getApiUrl('nabu-gateway/users/current')
  })

export const displayBackupCodes = (body: TwoFactorFormType) =>
  fetchApi<TOtpConfigType>({
    auth: true,
    body: { code: body.twoFactorCode, password: body.password },
    method: 'PUT',
    url: getApiUrl('nabu-gateway/users/two-factor-auth/backup/display')
  })

export const linkWithExistingUser = () =>
  fetchApi<void>({
    auth: true,
    method: 'PUT',
    url: getApiUrl('nabu-gateway/users/link-account/existing/start')
  })

export const refreshBackupCodes = (body: TwoFactorFormType) =>
  fetchApi<TOtpConfigType>({
    auth: true,
    body: { code: body.twoFactorCode, password: body.password },
    method: 'PUT',
    url: getApiUrl('nabu-gateway/users/two-factor-auth/backup/refresh')
  })

export const resendCode = () =>
  fetchApi<void>({
    auth: true,
    method: 'PUT',
    url: getApiUrl('nabu-gateway/users/email/resend')
  })

export const saveSettings = (body: Partial<UserSettingsType>) => {
  return fetchApi<void>({
    auth: true,
    body: { settings: body },
    method: 'POST',
    url: getApiUrl('nabu-gateway/users/exchange-settings')
  })
}

export const transfers = () =>
  fetchApi<{
    transfers: Array<RawTransferType>
  }>({
    auth: true,
    method: 'GET',
    url: getApiUrl('nabu-gateway/users/transfers')
  })

export const twoFactorStatus = () =>
  fetchApi<{
    twoFactors: Array<TwoFAType>
  }>({
    auth: true,
    method: 'GET',
    url: getApiUrl('nabu-gateway/users/two-factor-auth/status')
  })

export const updateUserPersonalInfo = (body: {
  dob: string
  firstName: string
  lastName: string
}) =>
  fetchApi<void>({
    auth: true,
    body,
    method: 'PUT',
    url: getApiUrl('nabu-gateway/users/current')
  })

export const updateAddress = (body: {
  city: string
  country: string
  firstLine: string
  postcode: string
  secondLine: string
  state: string
}) =>
  fetchApi<void>({
    auth: true,
    body: {
      address: {
        city: body.city,
        country: body.country,
        line1: body.firstLine,
        line2: body.secondLine,
        postCode: body.postcode,
        state: body.state
      }
    },
    method: 'PUT',
    url: getApiUrl('nabu-gateway/users/current/address')
  })

export const updateEmail = (body: { email: string }) =>
  fetchApi<UpdateEmailResponseType>({
    auth: true,
    body: {
      email: body.email
    },
    method: 'PUT',
    url: getApiUrl('nabu-gateway/users/email')
  })

export const updateEmailVerify = (body: {
  code1: string
  code2: string
  verificationId1: string
  verificationId2: string
}) =>
  fetchApi<void>({
    auth: true,
    body,
    method: 'PUT',
    url: getApiUrl('nabu-gateway/users/email/verify')
  })

export const verifyEmailCode = (body: {
  emailVerificationCode: string
  emailVerificationId: string
}) =>
  fetchApi<void>({
    auth: true,
    method: 'PUT',
    url: getApiUrl(
      `nabu-gateway/users/verification-code/${body.emailVerificationId}/${body.emailVerificationCode}`
    )
  })

export const getLimits = () =>
  fetchApi<TiersLimitsVolumeType>({
    auth: true,
    method: 'GET',
    url: getApiUrl('nabu-gateway/users/limits')
  })

export const getFees = () =>
  fetchApi<FeesResponseType>({
    auth: true,
    method: 'GET',
    url: getApiUrl('nabu-gateway/users/fees')
  })

export const getExtraKYC = (context: ExtraKYCContext) =>
  fetchApi({
    auth: true,
    method: 'GET',
    url: getApiUrl(`nabu-gateway/kyc/extra-questions?context=${context}`)
  })

export const submitExtraKYC = (extraKYCQuestions: Array<Node>) =>
  fetchApi({
    auth: true,
    body: {
      nodes: extraKYCQuestions
    },
    method: 'PUT',
    url: getApiUrl('nabu-gateway/kyc/extra-questions')
  })

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getUkFrictionsState = (context) =>
  fetchApi({
    auth: true,
    method: 'GET',
    url: getApiUrl(`nabu-gateway/onboarding/uk-frictions/state`)
  })
